<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-card outlined id="nomination-draft-scope">
            <v-card-title class="primary white--text py-4">
               <v-icon color="white" class="mr-2">mdi-account-multiple-plus</v-icon>
               Member Nominations
            </v-card-title>

            <v-card-text class="pa-4">
               <v-row dense class="mb-2">
                  <v-col cols="12" md="4">
                     <v-card outlined class="pa-4 summary-card">
                        <div class="caption grey--text text--darken-1">Available Positions</div>
                        <div class="headline font-weight-medium">{{ positions.length }}</div>
                     </v-card>
                  </v-col>
                  <v-col cols="12" md="4">
                     <v-card outlined class="pa-4 summary-card">
                        <div class="caption grey--text text--darken-1">Draft Selected</div>
                        <div class="headline font-weight-medium">{{ draftCount }}</div>
                     </v-card>
                  </v-col>
                  <v-col cols="12" md="4">
                     <v-card outlined class="pa-4 summary-card">
                        <div class="caption grey--text text--darken-1">Submitted</div>
                        <div class="headline font-weight-medium">{{ submittedCount }}</div>
                     </v-card>
                  </v-col>
               </v-row>

               <v-alert type="info" text dense>
                  You can nominate up to 10 positions. Draft selections are saved in this browser so you can continue after refresh or login again.
                  Please check your email after submitting the nomination.
               </v-alert>
               <v-alert v-if="successMessage" type="success" dismissible @input="successMessage = ''">
                  {{ successMessage }}
               </v-alert>
               <v-alert v-if="errorMessage" type="error" dismissible @input="errorMessage = ''">
                  {{ errorMessage }}
               </v-alert>

               <v-data-table
                  :headers="headers"
                  :items="positions"
                  :loading="loading"
                  item-key="id"
                  disable-pagination
                  hide-default-footer
                  class="elevation-0 nomination-table"
               >
                  <template v-slot:item.number="{ index }">
                     {{ index + 1 }}
                  </template>

                  <template v-slot:item.title="{ item }">
                     <div class="font-weight-medium">{{ item.title }}</div>
                     <div v-if="item.summary" class="caption grey--text text--darken-1 mt-1">{{ item.summary }}</div>
                  </template>

                  <template v-slot:item.nominee="{ item }">
                     <v-autocomplete
                        v-model="selectionFor(item.id).nominee"
                        :items="memberOptions[item.id + '_nominee'] || []"
                        :loading="searchLoading[item.id] === 'nominee'"
                        :disabled="isSubmitted(item)"
                        item-text="fullname"
                        item-value="userid"
                        label="Search nominee"
                        placeholder="Type at least 2 characters"
                        prepend-inner-icon="mdi-account-search-outline"
                        outlined
                        dense
                        hide-details
                        clearable
                        no-filter
                        @focus="loadEligibleMembers(item.id, 'nominee')"
                        @update:search-input="searchMembers(item.id, 'nominee', $event)"
                        @change="onSelectionChange(item.id)"
                     >
                        <template v-slot:item="{ item: member }">
                           <v-list-item-avatar>
                              <v-img v-if="memberPhoto(member)" :src="memberPhoto(member)"></v-img>
                              <v-icon v-else>mdi-account-circle</v-icon>
                           </v-list-item-avatar>
                           <v-list-item-content>
                              <v-list-item-title>{{ member.fullname }}</v-list-item-title>
                              <v-list-item-subtitle>{{ member.memberid }} · {{ member.membertype }}</v-list-item-subtitle>
                           </v-list-item-content>
                        </template>
                        <template v-slot:append-item>
                           <div v-intersect="{ handler: () => loadMoreEligibleMembers(item.id, 'nominee'), options: { threshold: [0] } }"></div>
                        </template>
                     </v-autocomplete>
                  </template>

                  <template v-slot:item.seconder="{ item }">
                     <v-autocomplete
                        v-model="selectionFor(item.id).seconder"
                        :items="memberOptions[item.id + '_seconder'] || []"
                        :loading="searchLoading[item.id] === 'seconder'"
                        :disabled="isSubmitted(item)"
                        item-text="fullname"
                        item-value="userid"
                        label="Search seconder"
                        placeholder="Type at least 2 characters"
                        prepend-inner-icon="mdi-account-check-outline"
                        outlined
                        dense
                        hide-details
                        clearable
                        no-filter
                        @focus="loadEligibleMembers(item.id, 'seconder')"
                        @update:search-input="searchMembers(item.id, 'seconder', $event)"
                        @change="onSelectionChange(item.id)"
                     >
                        <template v-slot:item="{ item: member }">
                           <v-list-item-avatar>
                              <v-img v-if="memberPhoto(member)" :src="memberPhoto(member)"></v-img>
                              <v-icon v-else>mdi-account-circle</v-icon>
                           </v-list-item-avatar>
                           <v-list-item-content>
                              <v-list-item-title>{{ member.fullname }}</v-list-item-title>
                              <v-list-item-subtitle>{{ member.memberid }} · {{ member.membertype }}</v-list-item-subtitle>
                           </v-list-item-content>
                        </template>
                        <template v-slot:append-item>
                           <div v-intersect="{ handler: () => loadMoreEligibleMembers(item.id, 'seconder'), options: { threshold: [0] } }"></div>
                        </template>
                     </v-autocomplete>
                     <div v-if="rowErrors[item.id]" class="error--text caption mt-1">
                        {{ rowErrors[item.id] }}
                     </div>
                  </template>

                  <template v-slot:item.status="{ item }">
                     <v-chip :color="isSubmitted(item) ? 'success' : 'grey lighten-3'" :dark="isSubmitted(item)" small>
                        <v-icon left small>{{ isSubmitted(item) ? 'mdi-check-circle-outline' : 'mdi-pencil-outline' }}</v-icon>
                        {{ isSubmitted(item) ? 'Submitted' : 'Draft' }}
                     </v-chip>
                  </template>

                  <template v-slot:no-data>
                     <div class="empty-state py-10">
                        <v-icon size="44" color="blue-grey lighten-2">mdi-file-search-outline</v-icon>
                        <div class="mt-2">No nomination is available for your membership type or region.</div>
                     </div>
                  </template>
               </v-data-table>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
               <v-spacer></v-spacer>
               <v-btn text :disabled="submitting || draftCount === 0" @click="clearDraft">
                  <v-icon left>mdi-refresh</v-icon>Clear Draft
               </v-btn>
               <v-btn color="primary" :loading="submitting" :disabled="loading || positions.length === 0" @click="submitNominations">
                  <v-icon left>mdi-send</v-icon>Submit Nominations
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOMNominations',
   components: { DashboardLayout, BaseBreadcrumb },
   props: ['user'],
   data() {
      return {
         page: { title: 'Nominations', icon: 'mdi-account-multiple-plus' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Nominations', disabled: true },
         ],
         headers: [
            { text: 'No.', value: 'number', width: 65, sortable: false },
            { text: 'Position', value: 'title', width: '28%', sortable: false },
            { text: 'Nominee', value: 'nominee', width: '27%', sortable: false },
            { text: 'Seconder', value: 'seconder', width: '27%', sortable: false },
            { text: 'Status', value: 'status', width: 135, sortable: false },
         ],
         positions: [],
         selections: {},
         memberOptions: {},
         searchLoading: {},
         searchTimers: {},
         loading: true,
         submitting: false,
         successMessage: '',
         errorMessage: '',
         memberOffset: {},
         memberHasMore: {},
         rowErrors: {},
      }
   },
   computed: {
      draftKey() {
         return 'mip.nomination.page.' + this.user.id
      },
      submittedCount() {
         return this.positions.filter(this.isSubmitted).length
      },
      draftCount() {
         return this.positions.filter((position) => {
            if (this.isSubmitted(position)) return false
            const selection = this.selections[position.id] || {}
            return Boolean(selection.nominee || selection.seconder)
         }).length
      },
   },
   mounted() {
      this.loadDraft()
      this.loadPositions()
   },
   beforeDestroy() {
      Object.keys(this.searchTimers).forEach((key) => clearTimeout(this.searchTimers[key]))
   },
   methods: {
      selectionFor(positionId) {
         if (!this.selections[positionId]) {
            this.$set(this.selections, positionId, { nominee: null, seconder: null })
         }
         return this.selections[positionId]
      },
      onSelectionChange(positionId) {
         this.saveDraft()
         this.$set(this.rowErrors, positionId, this.validateSelection(positionId))
      },
      validateSelection(positionId) {
         const selection = this.selections[positionId] || {}
         if (!selection.nominee || !selection.seconder) return ''
         if (Number(selection.nominee) === Number(selection.seconder)) {
            return 'Nominee and seconder must be different.'
         }
         if (Number(this.user.id) === Number(selection.nominee) || Number(this.user.id) === Number(selection.seconder)) {
            return 'You cannot nominate or second yourself.'
         }
         return ''
      },
      isSubmitted(position) {
         return Number(position.was_submitted_by_user || 0) === 1
      },
      loadPositions() {
         this.loading = true
         axios.get('/member/nominations/list')
            .then(({ data }) => {
               this.positions = data.status === 'OK' && Array.isArray(data.data) ? data.data : []
               this.positions.forEach((position) => {
                  if (!this.isSubmitted(position)) {
                     this.selectionFor(position.id)
                     return
                  }
                  const options = [
                     { userid: Number(position.submitted_nominee_id), fullname: position.submitted_nominee },
                     { userid: Number(position.submitted_seconder_id), fullname: position.submitted_seconder },
                  ].filter((member) => member.userid && member.fullname)
                  this.$set(this.memberOptions, position.id, options)
                  this.$set(this.selections, position.id, {
                     nominee: Number(position.submitted_nominee_id),
                     seconder: Number(position.submitted_seconder_id),
                  })
               })
               if (data.status !== 'OK' && data.error !== 'No records') this.errorMessage = data.error || 'Unable to load nominations.'
            })
            .catch(() => { this.errorMessage = 'Unable to load nominations. Please try again.' })
            .finally(() => { this.loading = false })
      },
      searchMembers(positionId, role, value) {
         if (!value || typeof value !== 'string' || value.trim().length < 2) return
         const timerKey = positionId + '-' + role
         clearTimeout(this.searchTimers[timerKey])
         this.$set(this.searchTimers, timerKey, setTimeout(() => {
            this.$set(this.searchLoading, positionId, role)
            axios.get('/member/search/' + encodeURIComponent(value.trim()))
               .then(({ data }) => {
                  this.$set(this.memberOptions, positionId + '_' + role, Array.isArray(data.data) ? data.data.slice(0, 12) : [])
               })
               .catch(() => { this.errorMessage = 'Unable to search members. Please try again.' })
               .finally(() => { this.$set(this.searchLoading, positionId, null) })
         }, 350))
      },

      loadEligibleMembers(positionId, role) {
         var key = positionId + '_' + role
         if (this.memberOptions[key] && this.memberOptions[key].length) return
         this.$set(this.memberOffset, key, 0)
         this.fetchEligibleMembers(positionId, role, true)
      },
      loadMoreEligibleMembers(positionId, role) {
         var key = positionId + '_' + role
         if (this.memberHasMore[key] === false) return
         this.fetchEligibleMembers(positionId, role, false)
      },
      fetchEligibleMembers(positionId, role, reset) {
         var key = positionId + '_' + role
         var offset = this.memberOffset[key] || 0
         axios.get('/member/nomination/eligible/' + positionId + '?offset=' + offset)
            .then(({ data }) => {
               var results = Array.isArray(data.data) ? data.data : []
               var existing = reset ? [] : (this.memberOptions[key] || [])
               this.$set(this.memberOptions, key, existing.concat(results))
               this.$set(this.memberOffset, key, offset + results.length)
               this.$set(this.memberHasMore, key, results.length === 12)
            })
            .catch(() => {})
      },
      saveDraft() {
         const drafts = {}
         const memberCache = {}
         this.positions.forEach((position) => {
            if (this.isSubmitted(position) || !this.selections[position.id]) return
            const selection = this.selections[position.id]
            drafts[position.id] = selection
            ;['nominee', 'seconder'].forEach((role) => {
               const userid = selection[role]
               if (!userid) return
               const key = position.id + '_' + role
               const options = this.memberOptions[key] || []
               const member = options.find((m) => Number(m.userid) === Number(userid))
               if (member) memberCache[userid] = member
            })
         })
         localStorage.setItem(this.draftKey, JSON.stringify(drafts))
         localStorage.setItem(this.draftKey + '.members', JSON.stringify(memberCache))
      },
      loadDraft() {
         try {
            const stored = JSON.parse(localStorage.getItem(this.draftKey) || '{}')
            this.selections = stored && typeof stored === 'object' ? stored : {}

            const memberCache = JSON.parse(localStorage.getItem(this.draftKey + '.members') || '{}')
            Object.keys(this.selections).forEach((positionId) => {
               const selection = this.selections[positionId]
               ;['nominee', 'seconder'].forEach((role) => {
                  const userid = selection[role]
                  if (!userid || !memberCache[userid]) return
                  const key = positionId + '_' + role
                  this.$set(this.memberOptions, key, [memberCache[userid]])
               })
            })
         } catch (error) {
            this.selections = {}
         }
      },
      clearDraft() {
         if (!window.confirm('Clear all unsent nomination drafts?')) return
         const submitted = {}
         this.positions.forEach((position) => {
            if (this.isSubmitted(position)) submitted[position.id] = this.selections[position.id]
         })
         this.selections = submitted
         localStorage.removeItem(this.draftKey)
         localStorage.removeItem(this.draftKey + '.members')
      },
      submitNominations() {
         this.errorMessage = ''
         this.successMessage = ''
         const incomplete = []
         const nominations = []

         this.positions.forEach((position) => {
            if (this.isSubmitted(position)) return
            const selection = this.selections[position.id] || {}
            if (!selection.nominee && !selection.seconder) return
            if (!selection.nominee || !selection.seconder) {
               incomplete.push(position.title)
               return
            }
            const rowError = this.validateSelection(position.id)
            if (rowError) {
               incomplete.push(position.title + ' (' + rowError + ')')
               return
            }
            nominations.push({
               id: Number(position.id),
               proposer: Number(this.user.id),
               nominee: Number(selection.nominee),
               seconder: Number(selection.seconder),
            })
         })

         if (incomplete.length) {
            this.errorMessage = 'Please complete: ' + incomplete.join(', ')
            return
         }
         if (!nominations.length) {
            this.errorMessage = 'Please select a nominee and seconder before submitting.'
            return
         }
         if (nominations.length > 10) {
            this.errorMessage = 'You can only submit up to 10 nominations.'
            return
         }

         const form = new FormData()
         form.append('nomdata', JSON.stringify(nominations))
         this.submitting = true
         axios.post('/member/nomination/submit', form)
            .then(({ data }) => {
               if (data.status !== 'OK') {
                  this.errorMessage = data.error || 'Unable to submit nominations.'
                  return
               }
               this.successMessage = (data.message || 'Your nominations have been submitted.') + ' Please check your email after submitting the nomination.'
               localStorage.removeItem(this.draftKey)
               this.loadPositions()
            })
            .catch((error) => {
               this.errorMessage = error.response && error.response.data && error.response.data.message
                  ? error.response.data.message
                  : 'Unable to submit nominations. Please try again.'
            })
            .finally(() => { this.submitting = false })
      },
      memberPhoto(member) {
         if (!member || !member.photolink || member.photolink === 'null') return null
         if (/^(https?:)?\/\//.test(member.photolink) || member.photolink.charAt(0) === '/') return member.photolink
         return '/' + member.photolink
      },
   },
}
</script>

<style scoped>
.summary-card { min-height: 82px; }
.nomination-table { border: 1px solid #e0e0e0; border-radius: 4px; }
.empty-state { color: #607d8b; }
@media (max-width: 960px) {
   .nomination-table { overflow-x: auto; }
}
</style>
